import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

// SVG Thread Pattern Component
const ThreadPattern = () => {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-10 pointer-events-none"
      viewBox="0 0 1000 600"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
          <stop offset="50%" stopColor="#D4AF37" stopOpacity="1" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      <motion.path
        d="M0,100 Q250,50 500,100 T1000,100"
        stroke="url(#goldGradient)"
        strokeWidth="2"
        fill="none"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, ease: 'easeInOut' }}
      />
      <motion.path
        d="M0,200 Q250,150 500,200 T1000,200"
        stroke="url(#goldGradient)"
        strokeWidth="1.5"
        fill="none"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, delay: 0.3, ease: 'easeInOut' }}
      />
      <motion.path
        d="M0,300 Q250,350 500,300 T1000,300"
        stroke="url(#goldGradient)"
        strokeWidth="2"
        fill="none"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, delay: 0.6, ease: 'easeInOut' }}
      />
      <motion.path
        d="M0,400 Q250,350 500,400 T1000,400"
        stroke="url(#goldGradient)"
        strokeWidth="1"
        fill="none"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, delay: 0.9, ease: 'easeInOut' }}
      />
      <motion.path
        d="M0,500 Q250,550 500,500 T1000,500"
        stroke="url(#goldGradient)"
        strokeWidth="1.5"
        fill="none"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2, delay: 1.2, ease: 'easeInOut' }}
      />
    </svg>
  );
};

export const BrandStory = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-32 md:py-48 overflow-hidden"
      data-testid="brand-story-section"
    >
      {/* Parallax Background */}
      <motion.div
        className="absolute inset-0"
        style={{
          y: backgroundY,
          background: 'linear-gradient(135deg, #FDFBF7 0%, #F5F5F0 50%, #FFFDD0 100%)',
        }}
      />

      {/* Silk Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1612863234179-7e51d74c28a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxyZWQlMjBzaWxrJTIwZmFicmljJTIwd2F2ZSUyMHRleHR1cmUlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3Mjk4NjA4OHww&ixlib=rb-4.1.0&q=85")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'multiply',
        }}
      />

      {/* Thread Pattern Animation */}
      <ThreadPattern />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
        {/* Decorative Top Element */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-4">
            <div className="w-16 h-px bg-[#D4AF37]" />
            <span className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase">
              Our Story
            </span>
            <div className="w-16 h-px bg-[#D4AF37]" />
          </div>
        </motion.div>

        {/* Main Quote */}
        <motion.blockquote
          className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-relaxed mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="brand-story-quote"
        >
          "Devanand Vestment blends 
          <motion.span 
            className="text-[#800020] inline-block mx-2 font-semibold"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
          >
            timeless Indian tradition
          </motion.span> 
          with modern elegance. Every piece is crafted to celebrate the 
          <motion.span 
            className="text-[#8B4513] inline-block mx-2 font-semibold"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8 }}
          >
            beauty, confidence
          </motion.span> 
          and grace of women."
        </motion.blockquote>

        {/* Description */}
        <motion.p
          className="font-body text-lg md:text-xl text-[#1A1A1A]/70 max-w-2xl mx-auto leading-relaxed mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          From the intricate weaves of Banarasi silk to the contemporary cuts of 
          designer wear, we bring you collections that honor our heritage while 
          embracing modern sensibilities.
        </motion.p>

        {/* Decorative Divider */}
        <motion.div
          className="flex items-center justify-center gap-4"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
          <div className="w-3 h-3 rotate-45 border border-[#D4AF37]" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-3 gap-8 mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="text-center">
            <span className="font-heading text-4xl md:text-5xl text-[#800020] block">10+</span>
            <span className="font-body text-sm text-[#1A1A1A]/60 tracking-wider uppercase mt-2 block">
              Years of Excellence
            </span>
          </div>
          <div className="text-center">
            <span className="font-heading text-4xl md:text-5xl text-[#800020] block">5000+</span>
            <span className="font-body text-sm text-[#1A1A1A]/60 tracking-wider uppercase mt-2 block">
              Happy Customers
            </span>
          </div>
          <div className="text-center">
            <span className="font-heading text-4xl md:text-5xl text-[#800020] block">200+</span>
            <span className="font-body text-sm text-[#1A1A1A]/60 tracking-wider uppercase mt-2 block">
              Exclusive Designs
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
